import { postRequest } from '../../../shared/utils';
import { CONFIG_SCHEMA } from '../../../shared/services/config';

export type PpvLeaderIncludedValuesResult =
  | { htz: string; includedValues: Array<string | number> }
  | { errors: { message: string }[] };

async function fetchHtzToProfitCentre(params: { htz: string; screenGridId: number }): Promise<any> {
  const { htz, screenGridId } = params;

  const body = {
    columns: ['htz', 'pf_lc'],
    screenGridId,
    schema: 'npi',
    table: 'v_npi_steering',
    key: 'htz',
    value: htz,
    limitNumber: 1,
  };

  return await postRequest('/api/records', body);
}

async function fetchProfitCenterToUser(params: {
  configSchema: string;
  pcnrId: number;
  screenGridId: number;
}): Promise<any> {
  const { configSchema, pcnrId, screenGridId } = params;

  const body = {
    columns: ['pcnr_id', 'mbd_user { firstname lastname }'],
    screenGridId,
    schema: configSchema,
    table: 'mbd_users_to_pcnr',
    // Prefer filterModel + isAnyOf so numbers can remain numbers in the GraphQL _in list.
    filterModel: {
      items: [{ field: 'pcnr_id', value: [pcnrId], operator: 'isAnyOf' }],
      logicOperator: 'and',
    },
  };

  return await postRequest('/api/records', body);
}

export async function getPpvLeaderIncludedValues(params: {
  htz: string;
  screenGridId: number;
  configSchema?: string;
}): Promise<PpvLeaderIncludedValuesResult> {
  const { htz, screenGridId, configSchema = CONFIG_SCHEMA } = params;
  if (!htz) {
    return { errors: [{ message: 'htz NULL' }] };
  }

  const result = await fetchHtzToProfitCentre({ htz, screenGridId });
  if (result?.errors) {
    console.error(result.errors);
    return {
      errors: Array.isArray(result.errors) ? result.errors : [{ message: 'Invalid request' }],
    };
  }

  const [row] = result?.data?.npi_v_npi_steering ?? [];
  const pf_lc = row?.pf_lc;

  // If no profit center found, return empty list; UI will fall back to full dropdown options.
  if (pf_lc === null || pf_lc === undefined) {
    return { htz, includedValues: [] };
  }

  const pcnrId = Number(pf_lc);
  if (!Number.isFinite(pcnrId)) {
    return { htz, includedValues: [] };
  }

  const result2 = await fetchProfitCenterToUser({ configSchema, pcnrId, screenGridId });
  if (result2?.errors) {
    console.error(result2.errors);
    return {
      errors: Array.isArray(result2.errors) ? result2.errors : [{ message: 'Invalid request' }],
    };
  }

  const rows = result2?.data?.[`${configSchema}_mbd_users_to_pcnr`] ?? [];
  const includedValues = rows
    .map((item: any) => {
      const firstname = item?.mbd_user?.firstname ?? '';
      const lastname = item?.mbd_user?.lastname ?? '';
      return `${firstname} ${lastname}`.trim();
    })
    .filter((name: string) => name);

  return { htz, includedValues };
}
